import React, { useEffect, useReducer, useRef } from 'react';
import { useForm } from 'react-final-form';
import { identity } from 'ramda';

import ValidationIcon from 'js/components/common/Fields/Icons/ValidationIcon';

import {
    useRegistrationValidationEvent
} from 'js/app/components/main/Registration/hooks/useRegistrationValidationEvent';
import { clsx } from 'js/app/utils/clsx.util';
import { useUpdateEffect } from 'js/hooks/useUpdateEffect';

import { initialData, reducer, SET_DAY, SET_FULL_DATE, SET_MONTH, SET_YEAR } from '../../reducer';

import style from './InputDateForDesktopAndIOS.scss';
import InputShallowStyle from 'js/app/components/common/Fields/Input/components/InputShallow/InputShallow.scss';

export const InputDateForDesktopAndIOS = ({
    name,
    value = '',
    setIsInputFocus = identity,
    isInputFocus,
    isShowValidationIcon,
    meta = {},
    resetFieldState = identity
}) => {
    const form = useForm();
    const refDay = useRef();
    const refMonth = useRef();
    const refYear = useRef();

    const [state, dispatch] = useReducer(reducer, initialData);
    const handlerValidationEvent = useRegistrationValidationEvent({ fieldName: name });

    useEffect(() => {
        if (!value) { return; }

        dispatch({ type: SET_FULL_DATE, payload: value });

        setTimeout(() => {
            refDay.current?.blur();
            refMonth.current?.blur();
            refYear.current?.blur();
        }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useUpdateEffect(() => {
        form.change(name, state.fullDate);
    }, [state.fullDate, form, name]);

    useUpdateEffect(() => {
        if (state.day.length === 2) {
            refMonth.current?.focus();
        }
    }, [state.day]);

    useUpdateEffect(() => {
        if (state.month.length === 2) {
            refYear.current.focus();
        }

        if (state.month.length === 0) {
            refDay.current?.focus();
        }
    }, [state.month]);

    useUpdateEffect(() => {
        if (state.year.length === 0) {
            refMonth.current?.focus();
        }
    }, [state.year]);

    const handlerOnChange = ({ type, event }) => {
        const inputVal = event.target.value.replace(/[^0-9]/g, '');

        dispatch({ type, payload: inputVal });
    };

    const handlerOnFocus = ({ type, event }) => {
        setIsInputFocus(true);

        if (event.target.value || type === SET_DAY) {
            return;
        }

        if (type === SET_MONTH && !state.day) {
            refDay.current.focus();
        }

        if (type === SET_YEAR && !state.month) {
            refMonth.current?.focus();
        }
    };

    const handlerOnBlur = (event) => {
        if ([refDay.current, refMonth.current, refYear.current].includes(event.relatedTarget)) {
            return;
        }

        handlerValidationEvent();
        setIsInputFocus(false);
        form.mutators.focusField(name);
        form.blur(name);
    };

    const handlerOnClick = () => {
        if (isInputFocus) {
            return;
        }

        if (state.day.length < 2) {
            refDay.current?.focus();
        } else if (state.month.length < 2) {
            refMonth.current?.focus();
        } else {
            refYear.current?.focus();
        }
    };

    const getElementByTypeAndStep = ({ step, type }) => {
        const elements = [refDay, refMonth, refYear];
        const elementsIndex = {
            [SET_DAY]: 0,
            [SET_MONTH]: 1,
            [SET_YEAR]: 2
        };

        return elements[Math.min(2, Math.max(0, elementsIndex[type] + step))].current;
    };

    const handlerOnKeyDown = ({ type, event }) => {
        const { target, keyCode } = event;
        const { value: valueInput } = target;

        switch (keyCode) {
            case 8: {
                if (valueInput) { return; }

                getElementByTypeAndStep({ step: -1, type }).focus();
                break;
            }
            case 13: {
                form.blur(name);
                form.submit();

                break;
            }
            case 37: {
                if (target.selectionStart) { return; }
                event.preventDefault();

                const element = getElementByTypeAndStep({ step: -1, type });

                element.focus();

                if (type !== SET_DAY) {
                    element.setSelectionRange(element.value.length, element.value.length, 'forward');
                }
                break;
            }
            case 39: {
                if (target.selectionStart !== target.maxLength) { return; }
                event.preventDefault();

                const element = getElementByTypeAndStep({ step: 1, type });

                element.focus();

                if (type !== SET_YEAR) {
                    element.setSelectionRange(0, 0, 'forward');
                }
                break;
            }
            default: {
                // nothing to do
            }
        }
    };

    const handlerEvents = (type) => ({
        onKeyDown: (event) => handlerOnKeyDown({ type, event }),
        onFocus: (event) => handlerOnFocus({ type, event }),
        onChange: (event) => handlerOnChange({ type, event })
    });

    return (
        <div
            className={clsx(
                InputShallowStyle.input,
                style.container
            )}
            onBlur={handlerOnBlur}
            onClick={handlerOnClick}
        >
            <div
                className={clsx(
                    style.date,
                    { [style.isHidden]: !isInputFocus && !value.length }
                )}
            >
                <span className={clsx(style.placeholder, style.isDay, { hidden: state.day })}>DD</span>

                <input
                    ref={refDay}
                    className={style.input}
                    type="text"
                    inputMode="numeric"
                    value={state.day}
                    pattern="\d*"
                    maxLength="2"
                    data-testid="InputDateForDesktopAndIOS_day"
                    {...handlerEvents(SET_DAY)}
                />

                <span className={style.delimiter}>/</span>

                <span className={clsx(style.placeholder, style.isMonth, { hidden: state.month })}>MM</span>

                <input
                    ref={refMonth}
                    className={style.input}
                    type="text"
                    value={state.month}
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength="2"
                    data-testid="InputDateForDesktopAndIOS_month"
                    {...handlerEvents(SET_MONTH)}
                />

                <span className={style.delimiter}>/</span>

                <span className={clsx(style.placeholder, style.isYear, { hidden: state.year })}>YYYY</span>

                <input
                    ref={refYear}
                    className={clsx(style.input, style.isYear)}
                    type="text"
                    inputMode="numeric"
                    value={state.year}
                    pattern="\d*"
                    maxLength="4"
                    data-testid="InputDateForDesktopAndIOS_year"
                    {...handlerEvents(SET_YEAR)}
                />
            </div>

            {isShowValidationIcon && (meta.touched || value) && !isInputFocus && (
                <ValidationIcon
                    className={style.validationIcon}
                    isValid={!meta.error}
                    animate
                    onClick={() => resetFieldState(name)}
                />
            )}
        </div>
    );
};
